import React, { useState, useEffect } from 'react';
import AnimatedLogo from './AnimatedLogo';

interface Props { children: React.ReactNode }

function useDemoTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem('ce_demo_theme') !== 'light');
  const toggle = () => setDark(d => {
    const next = !d;
    localStorage.setItem('ce_demo_theme', next ? 'dark' : 'light');
    return next;
  });
  return { dark, toggle };
}

const PhoneIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.94a16 16 0 0 0 6.06 6.06l1.06-1.06a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.03z"/>
  </svg>
);

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

const EyeIcon = ({ open }: { open: boolean }) => open ? (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
) : (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);


const SynergyLogo = ({ width = 80 }: { width?: number }) => {
  const h = Math.round(width * 84 / 161);
  return (
    <svg viewBox="0 0 161 84" width={width} height={h} xmlns="http://www.w3.org/2000/svg" aria-label="CallEssential by Synergytalk">
      <style>{`
        @keyframes stBar0{0%,100%{transform:scaleY(0.28)}50%{transform:scaleY(0.62)}}
        @keyframes stBar1{0%,100%{transform:scaleY(0.42)}50%{transform:scaleY(0.90)}}
        @keyframes stBar2{0%,100%{transform:scaleY(0.20)}50%{transform:scaleY(0.50)}}
        @keyframes stBar3{0%,100%{transform:scaleY(0.55)}50%{transform:scaleY(1.00)}}
        @keyframes stBar4{0%,100%{transform:scaleY(0.65)}50%{transform:scaleY(1.00)}}
        @keyframes stBar5{0%,100%{transform:scaleY(0.48)}50%{transform:scaleY(0.92)}}
        @keyframes stBar6{0%,100%{transform:scaleY(0.32)}50%{transform:scaleY(0.72)}}
        @keyframes stBar7{0%,100%{transform:scaleY(0.18)}50%{transform:scaleY(0.44)}}
        @keyframes stBar8{0%,100%{transform:scaleY(0.40)}50%{transform:scaleY(0.80)}}
        @keyframes stBar9{0%,100%{transform:scaleY(0.25)}50%{transform:scaleY(0.58)}}
      `}</style>
      <circle cx="16"  cy="18" r="3.5" fill="#7c3aed" opacity="0.75"/>
      <circle cx="147" cy="16" r="5"   fill="#fb923c" opacity="0.90"/>
      <circle cx="139" cy="10" r="2.5" fill="#3b82f6" opacity="0.70"/>
      <rect x="24"  y="10" width="8" height="64" rx="3" fill="#d946ef" style={{transformOrigin:'28px 42px',animation:'stBar0 0.85s 0.00s infinite ease-in-out'}}/>
      <rect x="36"  y="10" width="8" height="64" rx="3" fill="#a855f7" style={{transformOrigin:'40px 42px',animation:'stBar1 1.10s 0.18s infinite ease-in-out'}}/>
      <rect x="48"  y="10" width="8" height="64" rx="3" fill="#eab308" style={{transformOrigin:'52px 42px',animation:'stBar2 0.78s 0.35s infinite ease-in-out'}}/>
      <rect x="60"  y="10" width="8" height="64" rx="3" fill="#facc15" style={{transformOrigin:'64px 42px',animation:'stBar3 1.20s 0.06s infinite ease-in-out'}}/>
      <rect x="72"  y="10" width="8" height="64" rx="3" fill="#3b82f6" style={{transformOrigin:'76px 42px',animation:'stBar4 0.95s 0.50s infinite ease-in-out'}}/>
      <rect x="84"  y="10" width="8" height="64" rx="3" fill="#facc15" style={{transformOrigin:'88px 42px',animation:'stBar5 1.05s 0.22s infinite ease-in-out'}}/>
      <rect x="96"  y="10" width="8" height="64" rx="3" fill="#06b6d4" style={{transformOrigin:'100px 42px',animation:'stBar6 1.15s 0.62s infinite ease-in-out'}}/>
      <rect x="108" y="10" width="8" height="64" rx="3" fill="#22d3ee" style={{transformOrigin:'112px 42px',animation:'stBar7 0.82s 0.12s infinite ease-in-out'}}/>
      <rect x="120" y="10" width="8" height="64" rx="3" fill="#3b82f6" style={{transformOrigin:'124px 42px',animation:'stBar8 1.00s 0.42s infinite ease-in-out'}}/>
      <rect x="132" y="10" width="8" height="64" rx="3" fill="#d946ef" style={{transformOrigin:'136px 42px',animation:'stBar9 0.90s 0.28s infinite ease-in-out'}}/>
    </svg>
  );
};
export default function DemoLoginGate({ children }: Props) {
  const { dark, toggle } = useDemoTheme();
  const [authorized, setAuthorized] = useState(() => sessionStorage.getItem('demo_auth') === '1');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/demo/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        sessionStorage.setItem('demo_auth', '1');
        if (data.demo_token) sessionStorage.setItem('demo_token', data.demo_token);
        setAuthorized(true);
      } else {
        setError('Invalid username or password. Please try again.');
      }
    } catch {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  if (authorized) return <>{children}</>;

  /* ── Design tokens matching callessential.cc ── */
  const t = dark ? {
    bg:          '#000000',
    bgSecondary: '#0f0f1a',
    card:        'rgba(255,255,255,0.04)',
    border:      'rgba(255,255,255,0.08)',
    text:        '#f8fafc',
    textMuted:   '#94a3b8',
    textDim:     '#64748b',
    input:       'rgba(255,255,255,0.05)',
    inputBorder: 'rgba(255,255,255,0.12)',
    navBg:       'rgba(0,0,0,0.8)',
    gridLine:    'rgba(99,102,241,0.05)',
    shadow:      '0 32px 80px rgba(0,0,0,0.6)',
    themeBtnBg:  'rgba(255,255,255,0.06)',
    themeBtnBorder: 'rgba(255,255,255,0.1)',
  } : {
    bg:          '#f0f4ff',
    bgSecondary: '#e8eef8',
    card:        'rgba(255,255,255,0.85)',
    border:      'rgba(15,23,42,0.1)',
    text:        '#0f172a',
    textMuted:   '#475569',
    textDim:     '#64748b',
    input:       'rgba(255,255,255,0.8)',
    inputBorder: 'rgba(15,23,42,0.15)',
    navBg:       'rgba(240,244,255,0.9)',
    gridLine:    'rgba(99,102,241,0.08)',
    shadow:      '0 8px 32px rgba(15,23,42,0.12)',
    themeBtnBg:  'rgba(15,23,42,0.06)',
    themeBtnBorder: 'rgba(15,23,42,0.15)',
  };

  const gridBg = `linear-gradient(${t.gridLine} 1px, transparent 0), linear-gradient(90deg, ${t.gridLine} 1px, transparent 0)`;

  return (
    <div style={{
      minHeight: '100vh',
      background: t.bg,
      backgroundImage: gridBg,
      backgroundSize: '64px 64px',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'Inter, system-ui, sans-serif',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Radial glow */}
      <div style={{
        position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)',
        width: '80%', height: '60%', borderRadius: '50%',
        background: 'radial-gradient(ellipse at center, rgba(99,102,241,0.22) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-10%', right: '-5%',
        width: '40%', height: '40%', borderRadius: '50%',
        background: 'radial-gradient(ellipse at center, rgba(139,92,246,0.12) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />

      {/* Nav */}
      <nav style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '18px 32px',
        background: t.navBg,
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${t.border}`,
        position: 'relative', zIndex: 10,
      }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <AnimatedLogo width={40} />
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: 'rgba(255,255,255,0.92)', letterSpacing: 0.2, fontFamily: 'Inter, Arial, sans-serif' }}>
                CallEssential
              </div>
              <div style={{ fontSize: 9.5, fontWeight: 500, color: 'rgba(255,255,255,0.45)', letterSpacing: 0.3, fontFamily: 'Inter, Arial, sans-serif' }}>
                powered by Synergytalk
              </div>
            </div>
          </div>
        <button onClick={toggle} title="Toggle theme" style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '7px 14px', borderRadius: 8,
          background: t.themeBtnBg, border: `1px solid ${t.themeBtnBorder}`,
          color: t.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 500,
        }}>
          {dark ? <SunIcon /> : <MoonIcon />}
          {dark ? 'Light' : 'Dark'}
        </button>
      </nav>

      {/* Card */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 20px', position: 'relative', zIndex: 5,
      }}>
        <div style={{
          width: '100%', maxWidth: 420,
          background: t.card,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${t.border}`,
          borderRadius: 20,
          padding: '44px 40px',
          boxShadow: t.shadow,
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(99,102,241,0.15)',
              border: '1px solid rgba(99,102,241,0.3)',
              borderRadius: 999, padding: '5px 16px',
              fontSize: 11, color: '#a5b4fc', fontWeight: 500,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              marginBottom: 16,
            }}>Live Demo Environment</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: t.text, marginBottom: 6 }}>Demo Access</div>
            <div style={{ fontSize: 13, color: t.textMuted }}>Sign in to explore the live platform</div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: t.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Username</label>
              <input
                type="text" required autoComplete="username"
                value={username} onChange={e => setUsername(e.target.value)}
                placeholder="Enter username"
                style={{
                  width: '100%', padding: '11px 14px', borderRadius: 10,
                  background: t.input, border: `1px solid ${t.inputBorder}`,
                  color: t.text, fontSize: 14, outline: 'none', boxSizing: 'border-box',
                  fontFamily: 'inherit',
                }}
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: t.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'} required autoComplete="current-password"
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Enter password"
                  style={{
                    width: '100%', padding: '11px 44px 11px 14px', borderRadius: 10,
                    background: t.input, border: `1px solid ${t.inputBorder}`,
                    color: t.text, fontSize: 14, outline: 'none', boxSizing: 'border-box',
                    fontFamily: 'inherit',
                  }}
                />
                <button type="button" onClick={() => setShowPass(v => !v)} style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 0,
                }}>
                  <EyeIcon open={showPass} />
                </button>
              </div>
            </div>

            {error && (
              <div style={{
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
                borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                fontSize: 13, color: '#fca5a5', textAlign: 'center',
              }}>{error}</div>
            )}

            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '13px',
              background: loading ? 'rgba(99,102,241,0.5)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
              border: 'none', borderRadius: 10, color: '#fff',
              fontSize: 15, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s', fontFamily: 'inherit',
            }}>
              {loading ? 'Signing in…' : 'Access Demo Platform →'}
            </button>
          </form>

          <div style={{ marginTop: 28, textAlign: 'center', borderTop: `1px solid ${t.border}`, paddingTop: 20 }}>
            <p style={{ fontSize: 12, color: t.textDim, margin: 0 }}>
              Need access?{' '}
              <a href="https://www.callessential.cc/book-demo" style={{ color: '#818cf8', textDecoration: 'none' }}>Book a demo</a>
              {' · '}
              <a href="mailto:sales@synergytalk.ae" style={{ color: '#818cf8', textDecoration: 'none' }}>Contact sales</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
